// ============================================================================
// PRIVACY POLICY — public page (/privacy)
// Skeleton with placeholders. Have this reviewed by a lawyer before going live.
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from './LegalLayout';
import companyInfo, { formattedAddress } from './companyInfo';

const Privacy = () => (
  <LegalLayout
    title="Privacy Policy"
    subtitle={`How ${companyInfo.legalName} collects, uses and protects personal data.`}
  >
    <section>
      <h2>1. Scope</h2>
      <p>
        This policy explains how {companyInfo.legalName} (&ldquo;we&rdquo;) handles personal
        data in connection with the {companyInfo.brandName} platform. It covers two different
        roles:
      </p>
      <ul>
        <li>
          <strong>As a data controller</strong> — for the account and billing information of
          the organisations that subscribe to us, and the individual users they register.
        </li>
        <li>
          <strong>As a data processor</strong> — for the lead, customer and employee records
          that a subscribing organisation uploads into its own workspace. That organisation
          decides what is collected and why; we only process it to run the service on their
          instructions.
        </li>
      </ul>
    </section>

    <section>
      <h2>2. What we collect</h2>
      <h3>Account and user data</h3>
      <ul>
        <li>Name, username, email address and phone number of registered users</li>
        <li>Role, permissions and reporting hierarchy within the organisation</li>
        <li>Login history, IP address, device and browser information</li>
        <li>Push notification tokens, where notifications are enabled</li>
        <li>Attendance and location data, where those modules are enabled by the organisation</li>
      </ul>

      <h3>Billing data</h3>
      <ul>
        <li>Organisation legal name, billing address, GSTIN and billing contact</li>
        <li>Payment reference identifiers returned by our payment gateway</li>
      </ul>
      <p>
        We do <strong>not</strong> collect or store full card numbers, CVV, UPI PINs or net
        banking credentials. Those are captured directly by the payment gateway.
      </p>

      <h3>Customer data uploaded by the organisation</h3>
      <p>
        Lead and contact records — typically name, phone number, email, requirement details
        and interaction history — entered or imported by the subscribing organisation.
      </p>
    </section>

    <section>
      <h2>3. How we use it</h2>
      <ul>
        <li>To provide, maintain and secure the platform</li>
        <li>To authenticate users and enforce role-based access</li>
        <li>To process subscription payments and issue invoices</li>
        <li>To send service notifications, follow-up reminders and billing reminders</li>
        <li>To investigate misuse, security incidents and unauthorised access</li>
        <li>To meet legal, tax and regulatory obligations</li>
      </ul>
      <p>
        We do not sell personal data, and we do not use customer data uploaded by an
        organisation for our own marketing.
      </p>
    </section>

    <section>
      <h2>4. Legal basis</h2>
      <p>
        We process personal data to perform our contract with the subscribing organisation,
        to comply with legal obligations, and for our legitimate interests in securing and
        improving the service. Where consent is required, we obtain it.
      </p>
    </section>

    <section>
      <h2>5. Data isolation</h2>
      <p>
        Each subscribing organisation&rsquo;s records are stored in a separate database
        instance. The application resolves the correct instance per request, so one
        organisation cannot read another&rsquo;s data through the product.
      </p>
    </section>

    <section>
      <h2>6. Sharing and sub-processors</h2>
      <p>We share data only with the service providers needed to operate the platform:</p>
      <ul>
        <li>Payment gateway — to process subscription payments</li>
        <li>Cloud hosting provider — to host the application and databases</li>
        <li>Push notification provider — to deliver in-app and mobile notifications</li>
        <li>Email provider — to send transactional email</li>
      </ul>
      <p className="legal__placeholder">
        [LIST YOUR ACTUAL SUB-PROCESSORS BY NAME AND THE COUNTRY THEY PROCESS DATA IN]
      </p>
      <p>
        We may also disclose data where required by law, court order, or a valid request from
        a government authority.
      </p>
    </section>

    <section>
      <h2>7. Retention</h2>
      <p>
        We keep account and customer data for as long as the subscription is active. After a
        subscription lapses or is terminated, data is retained for{' '}
        <span className="legal__placeholder-inline">[RETENTION PERIOD, e.g. 90 days]</span> to
        allow reactivation, after which it is deleted or irreversibly anonymised.
      </p>
      <p>
        Billing records, invoices and tax documents are retained for the period required by
        Indian tax law regardless of account status.
      </p>
    </section>

    <section>
      <h2>8. Security</h2>
      <p>
        We use access controls, encrypted transport, hashed credentials, per-tenant database
        isolation, and audit logging of sensitive actions. No system is perfectly secure, but
        we take reasonable technical and organisational measures to protect data against loss
        and unauthorised access.
      </p>
      <p>
        If a breach affects your data, we will notify the affected organisation and the
        relevant authority as required by law.
      </p>
    </section>

    <section>
      <h2>9. Your rights</h2>
      <p>
        Subject to applicable law, you may request access to, correction of, or deletion of
        your personal data, and you may object to or restrict certain processing.
      </p>
      <p>
        If your data was uploaded into the platform by an organisation using our service,
        please direct your request to that organisation — they control it, and we will assist
        them in responding. Otherwise, write to {companyInfo.supportEmail}.
      </p>
    </section>

    <section>
      <h2>10. Cookies and local storage</h2>
      <p>
        We use browser local storage and session storage to hold authentication tokens, your
        workspace identifier, and interface preferences such as theme. These are required for
        the application to function. We do not use third-party advertising or tracking
        cookies.
      </p>
    </section>

    <section>
      <h2>11. Children</h2>
      <p>
        The service is intended for business use and is not directed at children. We do not
        knowingly collect personal data from anyone under 18.
      </p>
    </section>

    <section>
      <h2>12. Changes</h2>
      <p>
        We may update this policy. The current version is always published here with its
        effective date. Material changes will be notified to account administrators.
      </p>
    </section>

    <section>
      <h2>13. Grievance officer</h2>
      <p>
        In accordance with applicable Indian law, the grievance officer for privacy matters
        is:
      </p>
      <div className="legal__contact-block">
        <p className="legal__placeholder">[GRIEVANCE OFFICER NAME]</p>
        <p>{companyInfo.legalName}</p>
        <p>{formattedAddress}</p>
        <p>Email: {companyInfo.supportEmail}</p>
        <p>Phone: {companyInfo.supportPhone}</p>
      </div>
      <p>
        We aim to acknowledge complaints within 48 hours and resolve them within 30 days. You
        can also reach us via the <Link to="/contact">Contact</Link> page.
      </p>
    </section>
  </LegalLayout>
);

export default Privacy;
